import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import Button from "../../../../../components/shared/Button";
import Select from "../../../../../components/shared/inputs/Select";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { usePreferences } from "../../../../Settings/Configuration/hooks/usePreferences";
import { SCHEDULE_PREFERENCE_KEYS } from "../../constants/scheduleTypes";

function Pagination() {
    const { t } = useTranslation();
    const [filters, setFilters] = useFilters();
    const { totalItems, type } = useScheduleContext();
    const defaultPageSize = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.defaultPageSize`)

    const pagination = useMemo(() => ({
        pageIndex: Number(filters?.page || 1) - 1,
        pageSize: Number(filters?.pageSize || defaultPageSize || 50)
    }), [filters?.page, filters?.pageSize, defaultPageSize]);

    const totalPages = Math.ceil(totalItems / pagination.pageSize) || 1;
    const currentPage = pagination.pageIndex + 1;

    const firstItem = totalItems > 0 ? pagination.pageIndex * pagination.pageSize + 1 : 0;
    const lastItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalItems);

    const goToPage = useCallback((value: number) => {
        setFilters("page", String(value + 1));
    }, [setFilters]);

    const onPageSizeChange = useCallback((value: string) => {
        setFilters("pageSize", value);
    }, [setFilters]);

    useEffect(() => {
        if (!filters?.page && !filters?.pageSize) {
            setFilters("pageSize", String(defaultPageSize || 50));
        }
    }, [filters?.page, filters?.pageSize, defaultPageSize, setFilters]);

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-py-2">
            <div className="tw-flex-1 tw-text-muted-foreground tw-text-sm tw-gap-1 tw-flex">
                <span>{t("showing")}</span>
                <span>{firstItem}</span>
                <span>-</span>
                <span>{lastItem}</span>
                <span>{t("of")}</span>
                <span>{totalItems}</span>
            </div>

            <div className="tw-flex tw-items-center tw-space-x-6 lg:tw-space-x-8">
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <p className="tw-font-medium tw-text-sm">{t("rows-per-page")}</p>
                    <Select
                        className="tw-w-[70px]"
                        showSearch={false}
                        value={`${pagination.pageSize}`}
                        onChange={onPageSizeChange}
                    >
                        {[10, 20, 30, 40, 50, 100].map((pageSize) => (
                            <Select.Item key={pageSize} value={`${pageSize}`}>
                                {pageSize}
                            </Select.Item>
                        ))}
                    </Select>
                </div>
                <div className="tw-flex tw-w-[100px] tw-items-center tw-justify-center tw-font-medium tw-text-sm">
                    {t("page", {
                        current: currentPage,
                        total: totalPages,
                    })}
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <Button
                        variant="outline"
                        className="tw-hidden tw-h-8 tw-w-8 tw-p-0 lg:tw-flex"
                        onClick={() => goToPage(0)}
                        disabled={currentPage === 1}
                    >
                        <span className="tw-sr-only">{t("go-to-first-page")}</span>
                        <ChevronsLeft className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-h-8 tw-w-8 tw-p-0"
                        onClick={() => goToPage(pagination.pageIndex - 1)}
                        disabled={currentPage === 1}
                    >
                        <span className="tw-sr-only">{t("go-to-previous-page")}</span>
                        <ChevronLeft className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-h-8 tw-w-8 tw-p-0"
                        onClick={() => goToPage(pagination.pageIndex + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <span className="tw-sr-only">{t("go-to-next-page")}</span>
                        <ChevronRight className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-hidden tw-h-8 tw-w-8 tw-p-0 lg:tw-flex"
                        onClick={() => goToPage(totalPages - 1)}
                        disabled={currentPage === totalPages}
                    >
                        <span className="tw-sr-only">{t("go-to-last-page")}</span>
                        <ChevronsRight className="tw-h-4 tw-w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default Pagination;
